import type { VerificationChallenge } from '@tenfour/contracts'
// Admin API helper — uses admin JWT token stored in localStorage as 'tt_admin_token'
// This is separate from the account token ('tt_token') so admin and account
// sessions don't interfere with each other.
import {
  normalizeAdminPersonaRow,
  resolveAdminLoginResult,
  type AdminNeedsYouResolutionDetail,
  type AdminNeedsYouResolutionList,
  type AdminNeedsYouResolutionStats,
  type InferenceReviewItem,
  type InferenceReviewItemDetail,
  type InferenceReviewerRef,
  type ReviewHistoryFilters,
  type ReviewHistoryResponse,
  type ReviewInvoiceDetail,
  type ReviewInvoicePayload,
  type ReviewInvoiceRelease,
  type ReviewQueueItem,
  type ReviewStats,
  type ReviewState,
  type SubmitInferenceReview,
} from '@tenfour/contracts'
import { passkeyLogin } from '@/lib/passkey'
import { ADMIN_AUDIENCE, CONTRACTOR_AUDIENCE, refreshAccessToken } from '@/lib/auth-fetch'
import type { RefreshOutcome } from '@/lib/auth-session'

const API_BASE = import.meta.env.VITE_API_URL ?? '/api'

const ADMIN_TOKEN_KEY = 'tt_admin_token'
const ADMIN_REFRESH_KEY = 'tt_admin_refresh_token'
const ADMIN_USER_KEY = 'tt_admin_user'

function getAdminToken(): string | null {
  return localStorage.getItem(ADMIN_TOKEN_KEY)
}

function clearAdminSession(): void {
  localStorage.removeItem(ADMIN_TOKEN_KEY)
  localStorage.removeItem(ADMIN_REFRESH_KEY)
  localStorage.removeItem(ADMIN_USER_KEY)
}

// Rotates through the shared cross-tab refresh (lib/auth-session.ts): adminApi,
// adminFetchRaw, adminHelpApi and every other admin tab take one Web Lock, so
// the single-use admin refresh cookie is spent once per expiry and a tab that
// loses the race reuses the winner's token instead of signing out (#142). The
// POST itself (credentials: 'include', X-Auth-Audience: admin, empty body) is
// auth-fetch's, bound to ADMIN_AUDIENCE.
function refreshAdminToken(staleToken: string | null): Promise<RefreshOutcome> {
  return refreshAccessToken(ADMIN_AUDIENCE, staleToken)
}

export async function adminApi<T>(path: string, options?: RequestInit, retry = false): Promise<T> {
  const token = getAdminToken()
  const res = await fetch(`${API_BASE}${path}`, {
    // credentials:include so the httpOnly tt_refresh cookie rides every request
    // and the rotation on login/refresh works (#142).
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Auth-Audience': 'admin',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...options,
  })
  if (res.status === 204) return undefined as T
  // A 401 on the retry after a refresh that worked is the endpoint's own answer
  // (a wrong password, a failed passkey check), not a session verdict — the
  // refresh just vouched for the session — so it surfaces as an error below
  // instead of signing the admin out.
  if (res.status === 403 || (res.status === 401 && !retry)) {
    // A 401 is usually an expired access token — try one silent refresh and
    // retry before giving up, so the admin isn't bounced to login every ~15min.
    // 403 (not admin) and a failed/absent refresh fall through to logout.
    if (res.status === 401) {
      const outcome = await refreshAdminToken(token)
      if (outcome.status === 'rotated') return adminApi<T>(path, options, true)
      // No verdict from the refresh (offline, 5xx, another tab stuck
      // mid-rotation): fail this call but keep the admin signed in.
      if (outcome.status === 'unavailable') throw new Error('Could not refresh your session. Try again.')
    }
    clearAdminSession()
    if (typeof window !== 'undefined' && !window.location.pathname.includes('/admin/login')) {
      window.location.href = '/admin/login'
    }
    throw new Error('Session expired')
  }
  if (!res.ok) {
    // Surface the API's human message (NestJS puts it in `message`), not the
    // raw status + JSON blob. Falls back to a generic line for non-JSON bodies.
    let message = `Something went wrong (${res.status})`
    try {
      const body = await res.json()
      if (body?.message) message = Array.isArray(body.message) ? body.message.join(', ') : String(body.message)
    } catch {
      /* non-JSON error body — keep the generic message */
    }
    throw new Error(message)
  }
  return res.json()
}

// Refresh-aware raw fetch for non-JSON admin consumers (the Control Tower SSE).
// Shares adminApi's cross-tab refresh lock, so the stream open and a concurrent
// REST 401 — in this tab or another — don't both spend the cookie (#142).
export async function adminFetchRaw(path: string, options: RequestInit = {}): Promise<Response> {
  const open = (token: string | null) =>
    fetch(`${API_BASE}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        'X-Auth-Audience': 'admin',
        ...(options.headers as Record<string, string> | undefined),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
  const token = getAdminToken()
  let res = await open(token)
  if (res.status === 401) {
    const outcome = await refreshAdminToken(token)
    if (outcome.status === 'rotated') res = await open(outcome.token)
  }
  return res
}

// ── Auth ──────────────────────────────────────────────────────────────────────

/**
 * Admin password sign-in. Mirrors the contractor `AuthContext.signIn` so the one
 * shared login page behaves identically on both mounts: a TOTP challenge comes
 * back as `{ twoFactorRequired: true }` for the page to prompt on, and
 * `rememberDevice` trusts the browser for 30 days.
 *
 * Deliberately not routed through adminApi(): its 401 branch clears the session,
 * redirects, and replaces the API's message with "Session expired" — all wrong
 * for a failed sign-in attempt, which needs to say "Invalid email or password".
 */
export async function adminLogin(
  email: string,
  password: string,
  code?: string,
  rememberDevice?: boolean,
): Promise<{ twoFactorRequired: boolean; challenge?: VerificationChallenge }> {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    // credentials:include so a previously-set trusted-device cookie is sent (the
    // server then skips the 2FA prompt) and the rotated refresh cookie is stored.
    // X-Auth-Audience scopes that cookie to the admin session (#142).
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Auth-Audience': 'admin' },
    body: JSON.stringify({
      email,
      password,
      ...(code ? { code } : {}),
      ...(rememberDevice ? { rememberDevice: true } : {}),
    }),
  })

  if (!res.ok) {
    let message = 'Login failed'
    try {
      const body = await res.json()
      if (body?.message) message = Array.isArray(body.message) ? body.message.join(', ') : String(body.message)
    } catch {
      /* non-JSON error body — keep the generic message */
    }
    throw new Error(message)
  }

  const data: {
    token?: string
    user?: { id: string; email: string; name: string; role: string }
    twoFactorRequired?: boolean
  } = await res.json()

  const result = resolveAdminLoginResult(data)
  // 2FA is on and we didn't send a (valid) code yet — the page prompts for one.
  if (result.status === 'two_factor') return { twoFactorRequired: true, challenge: data as VerificationChallenge }
  if (result.status === 'not_admin') throw new Error('Admin access required')
  if (result.status !== 'ok') throw new Error('Login failed')

  localStorage.setItem(ADMIN_TOKEN_KEY, result.token)
  // The refresh token is no longer in the body — the cookie interceptor set it as
  // the httpOnly tt_refresh cookie, and refreshAdminToken rotates off it (#142).
  localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(result.user))
  return { twoFactorRequired: false }
}

// Sign in with a passkey on the admin portal. Runs the shared (role-agnostic)
// WebAuthn ceremony, then enforces the SAME role===ADMIN gate as adminLogin and
// stores the session under the admin localStorage keys. A non-admin passkey is
// rejected with the same error as a non-admin password login.
export async function adminPasskeyLogin(email?: string) {
  const data = await passkeyLogin(email)
  if (data.user.role !== 'ADMIN') {
    throw new Error('Admin access required')
  }
  const token = data.token || data.accessToken
  localStorage.setItem(ADMIN_TOKEN_KEY, token)
  // Refresh token rides the httpOnly cookie (see adminLogin / #142), not localStorage.
  localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(data.user))
  return data
}

export async function adminLogout() {
  // Clear the httpOnly admin refresh cookie server-side too (#142) — matches the
  // contractor signOut. Best-effort: local state is cleared regardless.
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-Auth-Audience': 'admin' },
    })
  } catch {
    /* network hiccup — still drop the local session below */
  }
  clearAdminSession()
}

export function getAdminUser(): { id: string; email: string; name: string; role: string } | null {
  const raw = localStorage.getItem('tt_admin_user')
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

// ── Stats ──────────────────────────────────────────────────────────────────────

export interface AdminStats {
  pendingInvoiceCount: number
  pendingApprovalCount: number
  totalAccounts: number
  inboundToday: number
  outboundToday: number
  activeConversationsToday: number
  oldestPendingAt: string | null
  queueStage: number
  approvedToday: number
  rejectedToday: number
  messageVolume: Array<{ day: string; inbound: number; outbound: number; approved: number; rejected: number }>
  avgResponseMs: number | null
  avgLlmMs: number | null
  slowCount: number
  agentActivity: Array<{ agentId: string; agentName: string; count: number }>
  twilioDailyCap: number
  twilioDailyCapRatio: number
}

export function getAdminStats(): Promise<AdminStats> {
  return adminApi('/admin/stats')
}

// ── Revenue ─────────────────────────────────────────────────────────────────

export interface AdminRevenue {
  totalRake: number
  totalLabor: number
  totalService: number
  totalMaterial: number
  feeCount: number
  byAccount: Array<{ accountId: string; name: string; businessName: string; rake: number; count: number }>
  recentFees: Array<{
    id: string
    invoiceId: string
    chargeId: string | null
    rakeAmount: number
    laborSubtotal: number
    serviceSubtotal: number
    materialSubtotal: number
    accountName: string
    createdAt: string
  }>
}

export function getAdminRevenue(): Promise<AdminRevenue> {
  return adminApi('/admin/revenue')
}

// ── Number-port requests (accounts bringing their existing number) ──────────
export type AdminPortRequest = {
  id: string
  phoneNumber: string
  customerType: string
  accountName: string
  currentCarrier: string | null
  accountNumber: string
  accountPin: string | null
  billingStreet: string
  billingCity: string
  billingState: string
  billingZip: string
  authorizedEmail: string
  status: 'SUBMITTED' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'CANCELED'
  statusNote: string | null
  createdAt: string
  account: { id: string; name: string; businessName: string }
}

export function listPortRequests(status?: string): Promise<AdminPortRequest[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : ''
  return adminApi(`/admin/port-requests${qs}`)
}

export function updatePortRequestStatus(
  id: string,
  body: { status: string; statusNote?: string },
): Promise<{ id: string; status: string }> {
  return adminApi(`/admin/port-requests/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
}

export type PortEncryptionStatus = {
  usingDevKey: boolean
  isProduction: boolean
  secure: boolean
  rowsUnderDevKey: number
}

// Encryption-health signal for the port-credential feature (#145).
export function getPortEncryptionStatus(): Promise<PortEncryptionStatus> {
  return adminApi('/admin/port-requests/encryption-status')
}

// ── Agent Stats ──────────────────────────────────────────────────────────────

export interface AgentStat {
  agentId: string
  agentName: string
  currentlyAssigned: number
  handledToday: number
  totalHandled: number
  avgResponseMs: number | null
  lastActiveAt: string | null
}

export function getAgentStats(): Promise<AgentStat[]> {
  return adminApi('/admin/agents/stats')
}

// ── Accounts ───────────────────────────────────────────────────────────────

export interface AdminAccount {
  id: string
  name: string
  businessName: string
  email: string
  ownerPhone: string
  phone: string | null
  onboardingStatus: string
  subscriptionStatus: string
  isTrial: boolean
  createdAt: string
  hourlyRate: number
  customerCount: number
  invoiceCount: number
  jobCount: number
  // True when the account has a Stripe Connect account but charges are
  // currently disabled. List view shows a red alert; detail panel shows the
  // reason text.
  stripeAlert: boolean
  stripeDisabledReason: string | null
}

export function listAdminAccounts(): Promise<AdminAccount[]> {
  return adminApi('/admin/accounts')
}

export interface AdminAccountDetailInvoice {
  id: string
  invoiceNumber: number
  customerName: string
  total: number
  status: string
  createdAt: string
}

export interface AdminAccountDetailConversation {
  id: string
  type: 'SMS' | 'VOICE'
  participantType: 'CUSTOMER' | 'CONTRACTOR' | 'UNKNOWN'
  customerName: string | null
  customerPhone: string | null
  lastMessage: { body: string; direction: string; createdAt: string } | null
  lastMessageAt: string | null
  messageCount: number
}

export interface AdminAccountDetailJob {
  id: string
  customerName: string
  customerPhone: string
  customerAddress: string | null
  description: string
  type: string
  status: string
  urgency: string
  notes: string | null
  completedAt: string | null
  createdAt: string
  updatedAt: string
  assignedUserName: string | null
  bookingAnswers?: import('@tenfour/contracts').StoredBookingAnswer[] | null
}

export interface AdminAccountDetailCustomer {
  id: string
  name: string
  phone: string
  address: string | null
  contactStatus: string
  totalJobs: number
  totalBilled: number
  lastSeenAt: string | null
  createdAt: string
}

export interface AdminAccountDetail extends AdminAccount {
  // Persona template variables (live contractors): {accountName} = name,
  // {businessName}, {tradeNoun}, {tradeDescription}. Editable on Settings.
  tradeNoun: string | null
  tradeDescription: string | null
  // "How did you hear about us?" ids from the last signup step. `[]` = the
  // prospect skipped it (the step is optional), not "no channel".
  signupAttribution: string[]
  // Twilio number identity + the A2P campaign it rides ('master' | 'demo').
  twilioPhoneNumberSid: string | null
  a2pCampaign: string
  conversationCount: number
  stripeAccountId: string | null
  stripeOnboardingComplete: boolean
  stripeChargesEnabled: boolean
  stripePayoutsEnabled: boolean
  stripeDisabledReason: string | null
  rakePercent: number
  subscriptionFee: number
  perInvoiceFee: number
  pendingSubscriptionFee: number | null
  pendingPerInvoiceFee: number | null
  pendingRateEffectiveAt: string | null
  // Plan cadence (plans/annual-billing-and-tier-page.md). subscriptionFee above
  // is the amount per RENEWAL — the annual figure when this is ANNUAL.
  subscriptionInterval: 'MONTHLY' | 'ANNUAL'
  pendingSubscriptionInterval: 'MONTHLY' | 'ANNUAL' | null
  subscriptionPrepaidThrough: string | null
  // Subscription tier (plans/subscription-tiers.md): named plan + a scheduled
  // downgrade + the GROWTH-tier monthly LLM budget override (null = platform default).
  tier: SubscriptionTier
  pendingTier: SubscriptionTier | null
  usageCapUsd: number | null
  isBeta: boolean
  smsDisabled: boolean
  // Invoice review gate: when on, Aria SMS/voice invoices are held IN_REVIEW for
  // Ten Four ops before the contractor sees them. Toggled on the Settings sub-page.
  requireInvoiceReview: boolean
  // The account's DEFAULT booking-form template id, or null when it uses the bundled
  // default. Surfaced + switchable on the Forms sub-page. (An account can be assigned
  // several forms; this is the one the booking page + Lyra resolve to.)
  defaultFormTemplateId: string | null
  currentCycle: {
    id: string
    periodStart: string
    periodEnd: string
    invoiceCount: number
    subscriptionFee: number
    feePerInvoice: number
  } | null
  recentInvoices: AdminAccountDetailInvoice[]
  recentConversations: AdminAccountDetailConversation[]
  recentJobs: AdminAccountDetailJob[]
  customers: AdminAccountDetailCustomer[]
  users: AdminAccountDetailUser[]
}

export interface AdminAccountDetailUser {
  id: string
  email: string | null
  name: string
  mobilePhone: string | null
  role: 'OWNER' | 'MEMBER'
  status: 'INVITED' | 'ACTIVE' | 'DISABLED'
  canGenerateInvoices: boolean
  canMarkInvoicePaid: boolean
  canCancelJobs: boolean
  notifyOnNewJob: boolean
  notifyOnNewJobChannel: string
  lastLoginAt: string | null
  createdAt: string
}

export interface AdminInviteMemberPayload {
  email: string
  name: string
  role?: 'OWNER' | 'MEMBER'
  mobilePhone?: string
  canGenerateInvoices?: boolean
  canMarkInvoicePaid?: boolean
  canCancelJobs?: boolean
}

export interface AdminUpdateMemberPayload {
  role?: 'OWNER' | 'MEMBER'
  status?: 'INVITED' | 'ACTIVE' | 'DISABLED'
  canGenerateInvoices?: boolean
  canMarkInvoicePaid?: boolean
  canCancelJobs?: boolean
  notifyOnNewJob?: boolean
  notifyOnNewJobChannel?: string
}

export function getAdminAccountDetail(id: string): Promise<AdminAccountDetail> {
  return adminApi(`/admin/accounts/${id}`)
}

// Team management on behalf of an account (mirrors the account-portal Team page,
// delegating to the same TeamService rules server-side).
export function adminInviteAccountUser(
  accountId: string,
  payload: AdminInviteMemberPayload,
): Promise<AdminAccountDetailUser> {
  return adminApi(`/admin/accounts/${accountId}/team/invite`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function adminUpdateAccountUser(
  accountId: string,
  userId: string,
  payload: AdminUpdateMemberPayload,
): Promise<AdminAccountDetailUser> {
  return adminApi(`/admin/accounts/${accountId}/team/users/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

export function adminDisableAccountUser(accountId: string, userId: string): Promise<unknown> {
  return adminApi(`/admin/accounts/${accountId}/team/users/${userId}`, { method: 'DELETE' })
}

// The config preset a persona is assigned to (a per-type PersonaTemplate). All
// per-account persona config now lives here, not on the persona row.
export type PersonaTemplateSummary = {
  name: string
  type: import('@tenfour/contracts').PersonaType
  displayName: string
  // Live prompt bodies flattened onto the template. null = use the bundled
  // default for this persona type at runtime.
  voiceGreeting: string | null
  voiceSystemPrompt: string | null
  smsSystemPrompt: string | null
  personaMessage: string | null
  llmOverride: import('@tenfour/contracts').PersonaLlmOverride
  voiceLlmOverride: import('@tenfour/contracts').PersonaVoiceLlmOverride
  tools: string[]
  // Outreach only: signup-detail field definitions the agent collects and the
  // public signup form renders. [] on every other persona type.
  formFields: import('@tenfour/contracts').OutreachFormField[]
  // Aria only: contractor-selectable web chat model tiers (the composer's
  // model picker). [] on every other persona type — and [] on aria hides it.
  chatModelTiers: import('@tenfour/contracts').AriaChatModelTier[]
  source: 'bundled' | 'custom'
}

export type PersonaTemplateListItem = PersonaTemplateSummary & {
  bundledRevision: number | null
  clonedFrom: string | null
  enabled: boolean
  updatedAt: string
  usedByAccountCount: number
  usedByAccountLabels: string[]
  // Outreach templates are used by NUMBERS, not account personas.
  usedByNumberCount: number
  usedByNumberLabels: string[]
}

// A persona row is now an assignment: which template the account uses for a type.
export type AdminPersonaRow = {
  type: import('@tenfour/contracts').PersonaType
  personaTemplateName: string
  template: PersonaTemplateSummary
}

export type PatchContractorPersonaUpdate = {
  type: import('@tenfour/contracts').PersonaType
  personaTemplateName: string
}

// Editable config payload for create/overwrite of a persona template.
export type PersonaTemplateConfigPayload = {
  displayName: string
  voiceGreeting?: string | null
  voiceSystemPrompt?: string | null
  smsSystemPrompt?: string | null
  personaMessage?: string | null
  llmOverride?: import('@tenfour/contracts').PersonaLlmOverride
  voiceLlmOverride?: import('@tenfour/contracts').PersonaVoiceLlmOverride
  tools?: string[]
  formFields?: import('@tenfour/contracts').OutreachFormField[]
  chatModelTiers?: import('@tenfour/contracts').AriaChatModelTier[]
}

export function getPersonaTemplates(
  type?: import('@tenfour/contracts').PersonaType,
): Promise<{ templates: PersonaTemplateListItem[] }> {
  return adminApi(`/admin/persona-templates${type ? `?type=${type}` : ''}`)
}

export function getPersonaTemplate(name: string): Promise<PersonaTemplateListItem> {
  return adminApi(`/admin/persona-templates/${name}`)
}

export function createPersonaTemplate(
  body: PersonaTemplateConfigPayload & {
    type: import('@tenfour/contracts').PersonaType
    name?: string
    copyFrom?: string
  },
): Promise<{ success: boolean; template: PersonaTemplateListItem }> {
  return adminApi('/admin/persona-templates', { method: 'POST', body: JSON.stringify(body) })
}

export function updatePersonaTemplate(
  name: string,
  body: PersonaTemplateConfigPayload,
): Promise<{ success: boolean; template: PersonaTemplateListItem }> {
  return adminApi(`/admin/persona-templates/${name}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export function resetBundledPersonaTemplate(
  name: string,
): Promise<{ success: boolean; template: PersonaTemplateListItem }> {
  return adminApi(`/admin/persona-templates/${name}/reset-bundled`, { method: 'POST' })
}

// Delete a template. When it's still assigned to accounts, pass `reassignTo`
// (another template of the same type) — the backend moves those personas onto it
// before dropping this one. Omit it for an unused template.
export function deletePersonaTemplate(
  name: string,
  reassignTo?: string,
): Promise<{ success: boolean }> {
  const qs = reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : ''
  return adminApi(`/admin/persona-templates/${name}${qs}`, { method: 'DELETE' })
}

// Persona template variables for a live account — the {tokens} that resolve in
// each persona's message + bound profile prompts on both voice and SMS. Edited
// on the account Settings sub-page; shown read-only in the persona editor.
export type AccountPersonaVariables = {
  businessName: string
  accountName: string
  tradeNoun: string | null
  trade: string | null
  tradeDescription: string | null
}

type AdminPersonasApiResponse = {
  account?: AccountPersonaVariables
  personas: Array<Parameters<typeof normalizeAdminPersonaRow>[0]>
}

function normalizeAdminPersonasResponse(res: AdminPersonasApiResponse): {
  account: AccountPersonaVariables
  personas: AdminPersonaRow[]
} {
  return {
    account: res.account ?? {
      businessName: '',
      accountName: '',
      tradeNoun: null,
      trade: null,
      tradeDescription: null,
    },
    personas: res.personas.map(normalizeAdminPersonaRow),
  }
}

export async function getAccountPersonas(
  accountId: string,
): Promise<{ account: AccountPersonaVariables; personas: AdminPersonaRow[] }> {
  const res = await adminApi<AdminPersonasApiResponse>(`/admin/accounts/${accountId}/personas`)
  return normalizeAdminPersonasResponse(res)
}

export async function patchAccountPersonas(
  accountId: string,
  updates: PatchContractorPersonaUpdate[],
): Promise<{ account: AccountPersonaVariables; personas: AdminPersonaRow[] }> {
  const res = await adminApi<AdminPersonasApiResponse>(`/admin/accounts/${accountId}/personas`, {
    method: 'PATCH',
    body: JSON.stringify({ updates }),
  })
  return normalizeAdminPersonasResponse(res)
}

// ── Named business-knowledge docs (account Settings) ─────────────────────────
// A doc's `name` IS the `{tag}` an admin writes in persona prompt bodies; the
// runtime mail-merges the content in like {businessName}. The list GET is
// metadata-only (docs can be 150KB); content loads lazily via the GET-one.

export type AdminKnowledgeDoc = {
  name: string
  fileName: string | null
  charCount: number
  enabled: boolean
  updatedBy: string | null
  updatedAt: string
}

export type AdminKnowledgeDocFull = AdminKnowledgeDoc & { content: string }

export function getAccountKnowledgeDocs(
  accountId: string,
): Promise<{ docs: AdminKnowledgeDoc[] }> {
  return adminApi(`/admin/accounts/${accountId}/knowledge-docs`)
}

export function getAccountKnowledgeDoc(
  accountId: string,
  name: string,
): Promise<AdminKnowledgeDocFull> {
  return adminApi(`/admin/accounts/${accountId}/knowledge-docs/${encodeURIComponent(name)}`)
}

export function putAccountKnowledgeDoc(
  accountId: string,
  name: string,
  body: { content: string; fileName?: string; enabled?: boolean },
): Promise<AdminKnowledgeDocFull> {
  return adminApi(`/admin/accounts/${accountId}/knowledge-docs/${encodeURIComponent(name)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export function patchAccountKnowledgeDoc(
  accountId: string,
  name: string,
  enabled: boolean,
): Promise<AdminKnowledgeDoc> {
  return adminApi(`/admin/accounts/${accountId}/knowledge-docs/${encodeURIComponent(name)}`, {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  })
}

export function deleteAccountKnowledgeDoc(
  accountId: string,
  name: string,
): Promise<{ deleted: boolean }> {
  return adminApi(`/admin/accounts/${accountId}/knowledge-docs/${encodeURIComponent(name)}`, {
    method: 'DELETE',
  })
}

export function assignTwilioNumber(
  id: string,
  params: {
    phone: string
    twilioPhoneNumberSid?: string
  },
): Promise<{
  id: string
  phone: string | null
  twilioPhoneNumberSid: string | null
  // True when a SID was supplied and we queued the master A2P campaign attach.
  attachQueued: boolean
}> {
  return adminApi(`/admin/accounts/${id}/twilio-number`, {
    method: 'PATCH',
    body: JSON.stringify(params),
  })
}

// Set the number's "main owner" — a team member (userId) whose mobilePhone
// becomes the account's ownerPhone, or a raw ownerPhone fallback.
export function setAccountNumberOwner(
  id: string,
  params: { userId?: string; ownerPhone?: string },
): Promise<{ id: string; ownerPhone: string }> {
  return adminApi(`/admin/accounts/${id}/owner`, {
    method: 'PATCH',
    body: JSON.stringify(params),
  })
}

export function setSmsDisabled(
  id: string,
  disabled: boolean,
): Promise<{ id: string; smsDisabled: boolean }> {
  return adminApi(`/admin/accounts/${id}/sms-disabled`, {
    method: 'PATCH',
    body: JSON.stringify({ disabled }),
  })
}

// Flip the invoice review gate: hold Aria SMS/voice invoices for Ten Four review
// before the contractor sees them. Default off; audited.
export function setInvoiceReviewGate(
  id: string,
  requireInvoiceReview: boolean,
): Promise<{ requireInvoiceReview: boolean }> {
  return adminApi(`/admin/accounts/${id}/invoice-review-gate`, {
    method: 'PATCH',
    body: JSON.stringify({ requireInvoiceReview }),
  })
}

// Edit identity + persona template variables ({businessName} / {accountName}
// = name / {tradeNoun} / {tradeDescription}). Partial update — send only the
// fields that changed.
export interface UpdateAccountProfilePayload {
  name?: string
  businessName?: string
  tradeNoun?: string
  tradeDescription?: string
}

export function updateAccountProfile(
  id: string,
  payload: UpdateAccountProfilePayload,
): Promise<{
  id: string
  name: string
  businessName: string
  tradeNoun: string | null
  tradeDescription: string | null
}> {
  return adminApi(`/admin/accounts/${id}/profile`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

export interface StuckOnboardingAccount {
  id: string
  name: string
  businessName: string
  email: string
  ownerPhone: string
  twilioPhoneNumberSid: string | null
  phone: string | null
  updatedAt: string
  createdAt: string
  deadEvents: Array<{
    eventType: string
    lastError: string | null
    attempts: number
    createdAt: string
  }>
}

export function getStuckOnboarding(): Promise<StuckOnboardingAccount[]> {
  return adminApi('/admin/stuck-onboarding')
}

export function restartAccountSetup(id: string): Promise<{ id: string; onboardingStatus: string }> {
  return adminApi(`/admin/accounts/${id}/restart-setup`, { method: 'POST' })
}

// Provision a fresh master-campaign number. Optional areaCode picks the NPA;
// omitted → the worker derives it from the owner's phone.
export function regenerateTwilioNumber(
  id: string,
  params?: { areaCode?: string },
): Promise<{ id: string; onboardingStatus: string }> {
  return adminApi(`/admin/accounts/${id}/regenerate-twilio`, {
    method: 'POST',
    body: JSON.stringify(params ?? {}),
  })
}

export function deleteAccount(id: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/accounts/${id}`, { method: 'DELETE' })
}

// ── Impersonation ("Log in as owner") ────────────────────────────────────────

export interface AdminImpersonateResponse {
  token: string
  user: {
    id: string
    email: string
    name: string
    role: string
    onboardingStatus?: string
    passwordResetCompleted?: boolean
    ssoLinked?: boolean
    userId?: string
    accountRole?: 'OWNER' | 'MEMBER'
  }
}

// Mint a contractor session for the account's owner and persist it under the
// contractor (tt_*) keys, so a freshly-opened /dashboard tab is authenticated as
// them. `credentials: 'include'` so the server's Set-Cookie (the httpOnly refresh
// token) is stored even cross-origin in dev (web :5173 → api :3000). The admin
// session (tt_admin_*) is left untouched — the two audiences use different keys.
export async function adminImpersonateAccount(id: string): Promise<AdminImpersonateResponse> {
  const data = await adminApi<AdminImpersonateResponse>(`/admin/accounts/${id}/impersonate`, {
    method: 'POST',
    credentials: 'include',
  })
  localStorage.setItem(CONTRACTOR_AUDIENCE.tokenKey, data.token)
  if (CONTRACTOR_AUDIENCE.userKey) {
    localStorage.setItem(CONTRACTOR_AUDIENCE.userKey, JSON.stringify(data.user))
  }
  return data
}

// ── Admin-only account lifecycle (#39, #34, #35) ──────────────────────────

export interface AdminInitialDiscountPayload {
  name: string
  discountType: 'percent' | 'fixed'
  percent?: number
  amount?: number
  appliesTo: Array<'subscription' | 'per_invoice'>
  startsAt?: string
  endsAt?: string
}

export interface AdminCreateAccountPayload {
  email: string
  password: string
  name: string
  businessName?: string
  ownerPhone?: string
  subscriptionFee?: number
  perInvoiceFee?: number
  isBeta?: boolean
  note?: string
  // Optional intro discount applied at account creation. Same tx — if the
  // account create fails, the discount doesn't land either.
  initialDiscount?: AdminInitialDiscountPayload
}

export function adminCreateAccount(
  payload: AdminCreateAccountPayload,
): Promise<{
  id: string
  email: string
  name: string
  businessName: string
  subscriptionFee: number
  perInvoiceFee: number
  isBeta: boolean
}> {
  return adminApi('/admin/accounts', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function updateBillingRate(
  id: string,
  payload: {
    subscriptionFee: number
    perInvoiceFee: number
    // ISO date — omit (or pass today) for an immediate change; pass a future
    // date to schedule a pending rate that promotes on the next cycle past
    // that date.
    effectiveAt?: string
    note?: string
  },
): Promise<{
  id: string
  subscriptionFee: number
  perInvoiceFee: number
  pendingSubscriptionFee: number | null
  pendingPerInvoiceFee: number | null
  pendingRateEffectiveAt: string | null
  scheduled: boolean
  changed: boolean
}> {
  return adminApi(`/admin/accounts/${id}/billing-rate`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

// ── Subscription tier (plans/subscription-tiers.md) ─────────────────────────

export type SubscriptionTier = 'GROWTH' | 'PRO' | 'MAX'

// Direct tier / per-account usage-limit overrides (#317). No charge, no fee
// change — pricing stays on the billing-rate endpoint. Explicit null on any
// override clears it (falls back to the tier default).
export function updateAccountTier(
  id: string,
  payload: {
    tier?: SubscriptionTier
    usageCapUsd?: number | null
    includedVoiceMinutesOverride?: number | null
    includedSmsMessagesOverride?: number | null
    note?: string
  },
): Promise<{
  id: string
  tier: SubscriptionTier
  pendingTier: SubscriptionTier | null
  usageCapUsd: number | null
  includedVoiceMinutesOverride: number | null
  includedSmsMessagesOverride: number | null
  changed: boolean
}> {
  return adminApi(`/admin/accounts/${id}/tier`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

// ── Tier definitions console (plans/tier-config-and-metered-usage.md) ────────
// Runtime per-tier config: price, feature matrix, usage allowances + overage
// rates. Absent override row = bundled default; reset deletes the row.

export interface TierConfig {
  tier: SubscriptionTier
  monthlyFeeUsd: number
  /** Percent off 12x the monthly price when billed annually. */
  annualDiscountPercent: number
  features: Record<string, boolean>
  includedVoiceMinutes: number | null
  includedSmsMessages: number | null
  /** Monthly AI (LLM inference) budget in USD; null = unlimited. */
  llmBudgetUsd: number | null
  voiceOverageRatePerHourUsd: number | null
  smsOverageRatePer100Usd: number | null
  /** Team group chats the tier may create; null = unlimited. */
  maxGroupChats: number | null
  /** AI meeting summaries per month; null = every meeting. */
  monthlyAiMeetings: number | null
  source: 'bundled' | 'custom'
}

export interface TierFeatureDescriptor {
  key: string
  label: string
  description: string
}

export interface TierConfigListEntry {
  tier: SubscriptionTier
  label: string
  bundled: TierConfig
  override: TierConfig | null
  effective: TierConfig
}

export interface TierConfigList {
  tiers: TierConfigListEntry[]
  featureRegistry: Record<string, TierFeatureDescriptor>
}

export interface TierConfigUpdate {
  monthlyFeeUsd: number
  annualDiscountPercent: number
  features: Record<string, boolean>
  includedVoiceMinutes: number | null
  includedSmsMessages: number | null
  llmBudgetUsd: number | null
  voiceOverageRatePerHourUsd: number | null
  smsOverageRatePer100Usd: number | null
  maxGroupChats: number | null
  monthlyAiMeetings: number | null
  note?: string
}

export interface TierConfigAuditEntry {
  id: string
  tier: SubscriptionTier
  actorId: string | null
  actorName: string | null
  action: 'tier_config_changed' | 'tier_config_reset'
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  note: string | null
  createdAt: string
}

export function getTierConfigs(): Promise<TierConfigList> {
  return adminApi('/admin/tier-configs')
}

export function putTierConfig(
  tier: SubscriptionTier,
  payload: TierConfigUpdate,
): Promise<{ ok: boolean; tier: SubscriptionTier }> {
  return adminApi(`/admin/tier-configs/${tier}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
}

export function resetTierConfig(
  tier: SubscriptionTier,
): Promise<{ ok: boolean; tier: SubscriptionTier }> {
  return adminApi(`/admin/tier-configs/${tier}`, { method: 'DELETE' })
}

export function getTierConfigAudit(limit = 20): Promise<{ entries: TierConfigAuditEntry[] }> {
  return adminApi(`/admin/tier-configs/audit?limit=${limit}`)
}

/** Ops view of one account's current-UTC-month usage (Pricing sub-page).
 *  Raw usage is always populated; allowances are null when the tier is
 *  unmetered for that unit. */
export interface AdminAccountUsageMeters {
  monthKey: string
  tier: SubscriptionTier
  isDemo: boolean
  /** included* = the EFFECTIVE allowance (tier config + this account's
   *  override, #317); override* = the account's own value (null = tier
   *  default); tierIncluded* = the tier config's value for context. */
  voice: {
    usedMinutes: number
    includedMinutes: number | null
    overageRatePerHourUsd: number | null
    overrideMinutes: number | null
    tierIncludedMinutes: number | null
  }
  sms: {
    usedMessages: number
    includedMessages: number | null
    overageRatePer100Usd: number | null
    overrideMessages: number | null
    tierIncludedMessages: number | null
  }
  llm: { monthUsd: number; capUsd: number | null; overCap: boolean; overrideUsd: number | null }
  manualMode: boolean
}

export function getAccountUsageMeters(id: string): Promise<AdminAccountUsageMeters> {
  return adminApi(`/admin/accounts/${id}/usage-meters`)
}

// ── Platform billing discounts (#36) ─────────────────────────────────────────

export interface PlatformBillingDiscount {
  id: string
  accountId: string
  name: string
  discountType: 'percent' | 'fixed'
  percent: number
  amount: number
  appliesTo: Array<'subscription' | 'per_invoice'>
  startsAt: string
  endsAt: string | null
  note: string | null
  isActiveNow: boolean
  createdAt: string
}

export function listDiscounts(accountId: string): Promise<PlatformBillingDiscount[]> {
  return adminApi(`/admin/accounts/${accountId}/discounts`)
}

export function createDiscount(
  accountId: string,
  payload: {
    name: string
    discountType: 'percent' | 'fixed'
    percent?: number
    amount?: number
    appliesTo: Array<'subscription' | 'per_invoice'>
    startsAt?: string
    endsAt?: string
    note?: string
  },
): Promise<PlatformBillingDiscount> {
  return adminApi(`/admin/accounts/${accountId}/discounts`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function updateDiscount(
  accountId: string,
  discountId: string,
  payload: Partial<{
    name: string
    discountType: 'percent' | 'fixed'
    percent: number
    amount: number
    appliesTo: Array<'subscription' | 'per_invoice'>
    startsAt: string
    endsAt: string
    note: string
  }>,
): Promise<PlatformBillingDiscount> {
  return adminApi(`/admin/accounts/${accountId}/discounts/${discountId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

export function deleteDiscount(
  accountId: string,
  discountId: string,
): Promise<{ success: boolean }> {
  return adminApi(`/admin/accounts/${accountId}/discounts/${discountId}`, {
    method: 'DELETE',
  })
}

// ── Account audit log (#38) ──────────────────────────────────────────────────

export interface AccountAuditEntry {
  id: string
  accountId: string
  actorType: 'ADMIN' | 'CONTRACTOR' | 'SYSTEM'
  actorId: string | null
  actorName: string | null
  action: string
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  note: string | null
  createdAt: string
}

export function getAuditLog(
  accountId: string,
  opts?: { limit?: number; cursor?: string },
): Promise<{ items: AccountAuditEntry[]; nextCursor: string | null }> {
  const params = new URLSearchParams()
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.cursor) params.set('cursor', opts.cursor)
  const qs = params.toString()
  return adminApi(`/admin/accounts/${accountId}/audit${qs ? `?${qs}` : ''}`)
}

// ── Admin activity feed (Control Tower) ──────────────────────────────────────

export type { AdminActivityItem, AdminFeedEvent, AdminConversationFeedItem } from '@tenfour/contracts'
import type { AdminActivityItem } from '@tenfour/contracts'

export interface ActivityFeedParams {
  cursor?: string
  accountId?: string
  integration?: string
  status?: string
  /** Persona filter for LLM calls: 'aria' | 'lyra' | 'shared' (llm-roles registry). */
  agent?: string
  /**
   * Finer persona-type filter for the per-persona view: 'lyra' | 'aria' |
   * 'memory_agent' | 'invoice_agent'. Scopes LLM rows to that persona's roles
   * and (for lyra/aria) integration rows to the matching send-recipient; the two
   * sub-agents are LLM-only. Pair with `accountId`.
   */
  persona?: string
  limit?: number
}

export function getActivityFeed(
  params: ActivityFeedParams = {},
): Promise<{ items: AdminActivityItem[]; nextCursor: string | null }> {
  const q = new URLSearchParams()
  if (params.cursor) q.set('cursor', params.cursor)
  if (params.accountId) q.set('accountId', params.accountId)
  if (params.integration) q.set('integration', params.integration)
  if (params.status) q.set('status', params.status)
  if (params.agent) q.set('agent', params.agent)
  if (params.persona) q.set('persona', params.persona)
  if (params.limit) q.set('limit', String(params.limit))
  const qs = q.toString()
  return adminApi(`/admin/activity/feed${qs ? `?${qs}` : ''}`)
}

export function getConversationActivity(conversationId: string): Promise<{ items: AdminActivityItem[] }> {
  return adminApi(`/admin/conversations/${conversationId}/activity`)
}

/** The admin JWT, exposed so the SSE feed client can set the Bearer header. */
export function adminToken(): string | null {
  return getAdminToken()
}

export const ADMIN_API_BASE = API_BASE

// ── Conversations ─────────────────────────────────────────────────────────────

export interface AdminConversation {
  id: string
  type: 'SMS' | 'VOICE'
  participantType: 'CUSTOMER' | 'CONTRACTOR' | 'UNKNOWN'
  accountId: string
  accountName: string
  businessName: string
  customerId: string | null
  customerName: string | null
  customerPhone: string | null
  lastMessage: { body: string; direction: string; createdAt: string } | null
  lastMessageAt: string | null
  updatedAt: string
  messageCount: number
  assignedAgentId?: string | null
  assignedAgentName?: string | null
  needsAction?: boolean
  isDemo?: boolean
}

export interface AdminConversationsResponse {
  conversations: AdminConversation[]
  hasMore: boolean
  nextCursor: string | null
}

export interface ListAdminConversationsParams {
  limit?: number
  cursor?: string
  participantType?: 'CUSTOMER' | 'CONTRACTOR'
  type?: 'SMS' | 'VOICE'
  accountId?: string
  q?: string
}

export function listAdminConversations(
  params?: ListAdminConversationsParams,
): Promise<AdminConversationsResponse> {
  const q = new URLSearchParams()
  if (params?.limit) q.set('limit', String(params.limit))
  if (params?.cursor) q.set('cursor', params.cursor)
  if (params?.participantType) q.set('participantType', params.participantType)
  if (params?.type) q.set('type', params.type)
  if (params?.accountId) q.set('accountId', params.accountId)
  if (params?.q?.trim()) q.set('q', params.q.trim())
  const qs = q.toString()
  return adminApi(`/admin/conversations${qs ? `?${qs}` : ''}`)
}

export interface AdminConversationDetail {
  conversation: {
    id: string
    type: 'SMS' | 'VOICE'
    participantType?: 'CUSTOMER' | 'CONTRACTOR' | 'UNKNOWN'
    participantPhone?: string | null
    accountId: string
    accountName: string
    businessName: string
    customerId: string | null
    customerName: string | null
    customerPhone: string | null
    isDemo?: boolean
  }
  messages: Array<{
    id: string
    body: string
    direction: string
    status: string
    createdAt: string
    fromPhone: string | null
    toPhone: string | null
    toolCall: any
    llmModel: string | null
    approvedBy: string | null
  }>
  llmLogs?: Array<{
    id: string
    toolCalls: any
    createdAt: string
    durationMs: number
    model: string
  }>
}

export function getConversationMessages(conversationId: string): Promise<AdminConversationDetail> {
  return adminApi(`/admin/conversations/${conversationId}/messages`)
}

export function adminSendMessage(conversationId: string, body: string): Promise<{ success: boolean; messageId: string }> {
  return adminApi(`/admin/conversations/${conversationId}/send`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  })
}

// ── Invoices ──────────────────────────────────────────────────────────────────

export interface AdminLineItem {
  id: string
  type: string
  name: string
  description: string | null
  quantity: number
  unitPrice: number
  total: number
  taxable: boolean
}

export interface AdminConversationMessage {
  id: string
  body: string
  direction: string
  createdAt: string
}

export interface AdminPastInvoice {
  id: string
  invoiceNumber: number
  total: number
  status: string
  createdAt: string
}

export interface AdminJob {
  id: string
  description: string
  status: string
  createdAt: string
}


export interface AdminAccountService {
  name: string
  price: number
  priceType: string
}

export interface AdminPendingInvoice {
  id: string
  invoiceNumber: number
  customerId: string | null
  customerName: string
  customerPhone: string | null
  total: number
  subtotal: number
  discountPercent: number
  discountAmount: number
  taxAmount: number
  taxRate: number
  taxState: string | null
  taxWarning: string | null
  taxBasis: string | null
  status: string
  // The contractor's triggering request (SMS text / voice summary) that produced
  // this held invoice — shown beside it so ops can compare intent vs output.
  reviewRequestText: string | null
  createdAt: string
  accountId: string
  accountName: string
  businessName: string
  assignedAgentId: string | null
  assignedAgentName: string | null
  accountHourlyRate: number
  accountServices: AdminAccountService[]
  customerAddress: string | null
  customerTotalJobs: number
  customerTotalBilled: number
  customerLastJobAt: string | null
  conversationMessages: AdminConversationMessage[]
  pastInvoices: AdminPastInvoice[]
  jobs: AdminJob[]
  lineItems: AdminLineItem[]
}

export function listPendingInvoices(search?: string): Promise<AdminPendingInvoice[]> {
  const qs = search ? `?search=${encodeURIComponent(search)}` : ''
  return adminApi(`/admin/invoices/pending${qs}`)
}

export interface AdminInvoiceHistoryItem {
  id: string
  invoiceNumber: number
  customerName: string
  total: number
  status: string
  createdAt: string
  updatedAt: string
  accountName: string
  businessName: string
  approvedByName: string | null
  approvedAt: string | null
}

// Zip.tax billed-call usage for the current billing cycle (#173) — the admin
// usage card + near-cap warning. Shape mirrors ZiptaxUsageSummary in contracts.
export interface ZiptaxUsage {
  periodStart: string
  resetsAt: string
  periodLabel: string
  calls: number
  limit: number
  percent: number
  level: 'ok' | 'warn' | 'over'
}

export function getZiptaxUsage(): Promise<ZiptaxUsage> {
  return adminApi('/admin/tax/ziptax-usage')
}

// Baseline the current cycle to the real number from the Zip.tax billing
// dashboard; our counter ticks up from there (#173).
export function syncZiptaxUsage(calls: number): Promise<ZiptaxUsage> {
  return adminApi('/admin/tax/ziptax-usage/sync', { method: 'POST', body: JSON.stringify({ calls }) })
}

export function listInvoiceHistory(limit = 25): Promise<AdminInvoiceHistoryItem[]> {
  return adminApi(`/admin/invoices/history?limit=${limit}`)
}

export interface RecentActivityItem {
  id: string
  type: 'message' | 'invoice'
  account: string
  customer: string
  summary: string
  toolCall: string | null
  status: string
  handledBy: string | null
  handledAt: string | null
  createdAt: string
  conversationId: string | null
}

export function listRecentActivity(limit = 50): Promise<RecentActivityItem[]> {
  return adminApi(`/admin/activity?limit=${limit}`)
}

export interface AdminInvoiceDetail {
  id: string
  invoiceNumber: number
  customerName: string
  customerPhone: string | null
  customerAddress: string | null
  total: number
  subtotal: number
  taxAmount: number
  taxRate: number
  status: string
  createdAt: string
  updatedAt: string
  paidAt: string | null
  sentAt: string | null
  accountName: string
  businessName: string
  lineItems: AdminLineItem[]
  conversationMessages: AdminConversationMessage[]
}

export function getInvoiceDetail(id: string): Promise<AdminInvoiceDetail> {
  return adminApi(`/admin/invoices/${id}`)
}

export function refundInvoice(id: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/invoices/${id}/refund`, { method: 'POST' })
}

export interface EditInvoicePayload {
  customerName?: string
  customerId?: string
  total?: number
  lineItems?: Array<{
    name: string
    description?: string
    type: string
    quantity: number
    unitPrice: number
    taxable?: boolean
  }>
}

export function editInvoice(id: string, payload: EditInvoicePayload): Promise<{ success: boolean }> {
  return adminApi(`/admin/invoices/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

// Release a held (IN_REVIEW) invoice to the contractor after review: approve
// (looked right / edited inline) or reject (flagged). Both → DRAFT + owner text.
export function reviewApproveInvoice(id: string): Promise<{ released: boolean; status: string }> {
  return adminApi(`/admin/invoices/${id}/review-approve`, { method: 'POST' })
}

export function reviewRejectInvoice(
  id: string,
  reason?: string,
): Promise<{ released: boolean; status: string }> {
  // The route runs under forbidNonWhitelisted — send `reason` or nothing at all.
  const trimmed = reason?.trim()
  return adminApi(`/admin/invoices/${id}/review-reject`, {
    method: 'POST',
    body: JSON.stringify(trimmed ? { reason: trimmed } : {}),
  })
}

// ── Queue System ─────────────────────────────────────────────────────────────

export function getQueueStage(): Promise<{ stage: number; autoApproveTools: string[] }> {
  return adminApi('/admin/queue/stage')
}

export function setQueueStage(stage: number): Promise<{ success: boolean; stage: number }> {
  return adminApi('/admin/queue/stage', { method: 'POST', body: JSON.stringify({ stage }) })
}

export function setAutoApproveTools(tools: string[]): Promise<{ success: boolean; autoApproveTools: string[] }> {
  return adminApi('/admin/queue/auto-approve-tools', { method: 'POST', body: JSON.stringify({ tools }) })
}

// ── LLM provider runtime switch ──────────────────────────────────────────────

export type LlmProviderKind = 'openai-responses' | 'openai-chat' | 'anthropic'

export function getLlmProviderSetting(): Promise<{
  override: LlmProviderKind | null      // null = no admin override, env is in effect
  envProvider: string | null            // raw env value (may be 'anthropic' or unset)
  effectiveProvider: string             // what the worker is actually running (may not match a UI card)
  source: 'env' | 'admin'               // whether the active provider came from DB or env
  validProviders: readonly LlmProviderKind[]
  envHints: {
    hasOpenAiApiKey: boolean
    hasOpenAiChatBaseUrl: boolean
    hasOpenAiChatApiKey: boolean
  }
}> {
  return adminApi('/admin/settings/llm-provider')
}

export function setLlmProviderSetting(
  provider: LlmProviderKind | null,
): Promise<{ success: boolean; provider: LlmProviderKind | null }> {
  return adminApi('/admin/settings/llm-provider', {
    method: 'POST',
    body: JSON.stringify({ provider }),
  })
}

// ── Models screen: per-role LLM config (Roles tab) ───────────────────────────

export type LlmRoleKind = 'loop' | 'single' | 'subagent'

export interface LlmRoleDescriptor {
  label: string
  description: string
  whereUsed: string
  kind: LlmRoleKind
  envDefault: string
}

export interface LlmRoleEffective {
  provider: LlmProviderKind
  model: string
  baseUrl: string | null
  credential: string | null
  capabilities: ModelCapabilities
  temperature: number | null
  toolChoice: 'auto' | 'required' | 'none' | null
  reasoningEffort: string | null
  maxIterations: number | null
  maxTokens: number | null
  fallbackProvider: LlmProviderKind | null
  fallbackModel: string | null
  enabled: boolean
}

export interface LlmRoleOverride {
  roleKey: string
  provider: string | null
  model: string | null
  temperature: number | null
  toolChoice: string | null
  maxTokens: number | null
  maxIterations: number | null
  reasoningEffort: string | null
  fallbackProvider: string | null
  fallbackModel: string | null
  enabled: boolean
  updatedAt: string
}

export interface LlmRoleConfigEntry {
  roleKey: string
  descriptor: LlmRoleDescriptor
  envDefault: string
  override: LlmRoleOverride | null
  effective: LlmRoleEffective
  orphaned: boolean
}

export interface LlmRoleConfigsResponse {
  roles: LlmRoleConfigEntry[]
  orphaned: Array<{ roleKey: string; override: LlmRoleOverride; orphaned: true }>
  providers: readonly LlmProviderKind[]
  toolChoices: ReadonlyArray<'auto' | 'required' | 'none'>
}

// Absent field = leave unchanged; explicit null = clear (inherit env default).
export type LlmRoleConfigUpdate = Partial<{
  provider: string | null
  model: string | null
  temperature: number | null
  toolChoice: string | null
  maxTokens: number | null
  maxIterations: number | null
  reasoningEffort: string | null
  fallbackProvider: string | null
  fallbackModel: string | null
  enabled: boolean
}>

export function getLlmRoleConfigs(): Promise<LlmRoleConfigsResponse> {
  return adminApi('/admin/llm/role-configs')
}

export function putLlmRoleConfig(
  roleKey: string,
  body: LlmRoleConfigUpdate,
): Promise<{ success: boolean; override: LlmRoleOverride; effective: LlmRoleEffective }> {
  return adminApi(`/admin/llm/role-configs/${roleKey}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export function deleteLlmRoleConfig(roleKey: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/llm/role-configs/${roleKey}`, { method: 'DELETE' })
}

// ── Models screen: usage / latency / waste / logs / pricing ──────────────────

function llmQuery(params: Record<string, string | number | boolean | undefined>): string {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&')
  return qs ? `?${qs}` : ''
}

export type LlmUsageGroupBy = 'day' | 'role' | 'model'

export interface LlmUsageRow {
  key: string
  calls: number
  promptTokens: number
  completionTokens: number
  cachedTokens: number
  estCostUsd: number
  errors: number
  fallbacks: number
  untracked: number
}

export interface LlmUsageResponse {
  from: string
  to: string
  groupBy: LlmUsageGroupBy
  rows: LlmUsageRow[]
}

export function getLlmUsage(params: {
  from?: string
  to?: string
  groupBy?: LlmUsageGroupBy
  accountId?: string
}): Promise<LlmUsageResponse> {
  return adminApi(`/admin/llm/usage${llmQuery(params)}`)
}

export interface LlmLatencyRow {
  key: string
  p50: number
  p95: number
  avg: number
  calls: number
}

export interface LlmLatencyResponse {
  from: string
  to: string
  byRole: LlmLatencyRow[]
  byModel: LlmLatencyRow[]
}

export function getLlmLatency(params: { from?: string; to?: string }): Promise<LlmLatencyResponse> {
  return adminApi(`/admin/llm/latency${llmQuery(params)}`)
}

export interface LlmWasteRole {
  key: string
  avgPromptTokens: number | null
  avgSystemChars: number | null
  avgMessageChars: number | null
  avgToolChars: number | null
  avgMessageCount: number | null
  calls: number
}

export interface LlmWasteConversation {
  conversationId: string
  accountName: string | null
  totalTokens: number
  calls: number
  estCostUsd: number
}

export interface LlmTopAccount {
  accountId: string
  accountName: string | null
  calls: number
  totalTokens: number
  estCostUsd: number
}

export interface LlmWasteResponse {
  from: string
  to: string
  roles: LlmWasteRole[]
  topConversations: LlmWasteConversation[]
  topAccounts: LlmTopAccount[]
}

export function getLlmWaste(params: { from?: string; to?: string }): Promise<LlmWasteResponse> {
  return adminApi(`/admin/llm/waste${llmQuery(params)}`)
}

export interface LlmCallRow {
  id: string
  role: string
  provider: string
  model: string
  accountId: string | null
  conversationId: string | null
  promptTokens: number | null
  completionTokens: number | null
  cachedTokens: number | null
  latencyMs: number
  success: boolean
  errorCode: string | null
  usedFallback: boolean
  streamed: boolean
  estCostUsd: number | null
  systemChars: number | null
  messageChars: number | null
  toolChars: number | null
  messageCount: number | null
  createdAt: string
}

export interface LlmCallsResponse {
  items: LlmCallRow[]
  total: number
  limit: number
  offset: number
}

export function getLlmCalls(params: {
  role?: string
  model?: string
  accountId?: string
  success?: boolean
  from?: string
  to?: string
  limit?: number
  offset?: number
}): Promise<LlmCallsResponse> {
  return adminApi(`/admin/llm/calls${llmQuery(params)}`)
}

// What dials a model accepts — drives the UI guards and the runtime.
export type TemperatureMode = 'flexible' | 'fixed' | 'none'
export type ToolChoiceMode = 'none' | 'auto' | 'forced'
export type MaxTokensParam = 'auto' | 'max_tokens' | 'max_completion_tokens'

export interface ModelCapabilities {
  temperature: TemperatureMode
  toolChoice: ToolChoiceMode
  reasoningEffort: string[] | null
  maxTokensParam: MaxTokensParam
  streaming: boolean
  // Accepts image input. Defaults FALSE (conservative — an image to a non-vision
  // model is a hard 400); enable per-model once support is confirmed.
  vision: boolean
}

// A catalog entry — a specific model with its full recipe (protocol/endpoint/
// credential/capabilities) + editable pricing. `provider` is the PROTOCOL.
export interface LlmPricingRow {
  id: string
  modelPattern: string
  provider: LlmProviderKind | null
  baseUrl: string | null
  credential: string | null
  label: string | null
  enabled: boolean
  capabilities: Partial<ModelCapabilities> | null // editable override (null = family default)
  effectiveCapabilities: ModelCapabilities // override merged over family default
  inputPerMtok: number
  outputPerMtok: number
  cachedInputPerMtok: number | null
  notes: string | null
  updatedAt: string
}

// Friendly labels for the protocol (adapter) — what the "openai-chat" etc.
// values actually mean.
export const PROTOCOL_LABELS: Record<LlmProviderKind, string> = {
  'openai-responses': 'Responses (OpenAI API)',
  'openai-chat': 'Chat Completions',
  anthropic: 'Anthropic Messages',
}

export function getLlmPricing(): Promise<LlmPricingRow[]> {
  return adminApi('/admin/llm/pricing')
}

export function upsertLlmPricing(body: {
  modelPattern: string
  provider?: string | null
  baseUrl?: string | null
  credential?: string | null
  label?: string | null
  enabled?: boolean
  capabilities?: Partial<ModelCapabilities> | null
  inputPerMtok: number | string
  outputPerMtok: number | string
  cachedInputPerMtok?: number | string | null
  notes?: string | null
}): Promise<LlmPricingRow> {
  return adminApi('/admin/llm/pricing', { method: 'POST', body: JSON.stringify(body) })
}

export function getLlmCapabilityDefaults(model: string): Promise<ModelCapabilities> {
  return adminApi(`/admin/llm/capabilities/defaults${llmQuery({ model })}`)
}

export function deleteLlmPricing(id: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/llm/pricing/${id}`, { method: 'DELETE' })
}

// ── Voice model catalog (voice_models) ──────────────────────────────────────
// The seeded "model objects" a persona's voice realtime override references by
// name. Parallels the text pricing catalog above, but for voice/realtime models.
export type VoiceModelProvider = 'xai' | 'openai' | 'elevenlabs'

export interface VoiceModelRow {
  name: string
  provider: VoiceModelProvider
  modelPattern: string
  voices: string[]
  capabilities: { reasoningEffort?: string[] | null } | null
  perMinUsd: number | null
  label: string | null
  enabled: boolean
  source: 'bundled' | 'custom'
  bundledRevision: number | null
  notes: string | null
  updatedAt: string
}

export function getVoiceModels(): Promise<VoiceModelRow[]> {
  return adminApi('/admin/llm/voice-models')
}

export function upsertVoiceModel(body: {
  name: string
  provider: string
  modelPattern: string
  voices: string[]
  capabilities?: { reasoningEffort?: string[] | null } | null
  perMinUsd?: number | null
  label?: string | null
  enabled?: boolean
  notes?: string | null
}): Promise<VoiceModelRow> {
  return adminApi('/admin/llm/voice-models', { method: 'POST', body: JSON.stringify(body) })
}

export function deleteVoiceModel(name: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/llm/voice-models/${encodeURIComponent(name)}`, { method: 'DELETE' })
}

export function resetBundledVoiceModel(name: string): Promise<VoiceModelRow> {
  return adminApi(`/admin/llm/voice-models/reset-bundled/${encodeURIComponent(name)}`, {
    method: 'POST',
  })
}

// Configurable credentials and whether each is present in env (never the value).
export interface LlmCredential {
  name: string
  present: boolean
}

export function getLlmCredentials(): Promise<{ credentials: LlmCredential[] }> {
  return adminApi('/admin/llm/credentials')
}

// Global default provider + model (the base every role inherits).
export interface LlmDefault {
  provider: LlmProviderKind | null
  model: string | null
  envProvider: string | null
}

export function getLlmDefault(): Promise<LlmDefault> {
  return adminApi('/admin/llm/default')
}

export function setLlmDefault(body: {
  provider: string | null
  model: string | null
}): Promise<{ success: boolean; provider: string | null; model: string | null }> {
  return adminApi('/admin/llm/default', { method: 'PUT', body: JSON.stringify(body) })
}

// Connectivity test for a {provider, model} (admin diagnostic).
export interface LlmModelTestResult {
  ok: boolean
  latencyMs: number
  model?: string
  reply?: string
  error?: string
}

export function testLlmModel(body: {
  provider: string
  model: string
  baseUrl?: string | null
  credential?: string | null
}): Promise<LlmModelTestResult> {
  return adminApi('/admin/llm/models/test', { method: 'POST', body: JSON.stringify(body) })
}

export interface UnpricedModel {
  model: string
  calls: number
}

export function getUnpricedModels(params: { from?: string; to?: string }): Promise<{ unpriced: UnpricedModel[] }> {
  return adminApi(`/admin/llm/models/unpriced${llmQuery(params)}`)
}

export interface LlmAccountUsage {
  from: string
  to: string
  totals: {
    calls: number
    promptTokens: number
    completionTokens: number
    cachedTokens: number
    estCostUsd: number
    errors: number
  }
  byRole: Array<{ key: string; calls: number; totalTokens: number; estCostUsd: number }>
  recent: LlmCallRow[]
}

export function getAccountLlmUsage(
  id: string,
  params: { from?: string; to?: string } = {},
): Promise<LlmAccountUsage> {
  return adminApi(`/admin/llm/accounts/${id}/usage${llmQuery(params)}`)
}

export interface LlmProviderErrorRate {
  provider: string
  calls: number
  errors: number
  errorRate: number
}

export interface LlmAlerts {
  dailyCostAlertUsd: number | null
  lastCostAlertDate: string | null
  todayCostUsd: number
  errorThreshold: number
  providers: LlmProviderErrorRate[]
}

export function getLlmAlerts(): Promise<LlmAlerts> {
  return adminApi('/admin/llm/alerts')
}

export function setLlmAlerts(dailyCostAlertUsd: number | null): Promise<{
  success: boolean
  dailyCostAlertUsd: number | null
}> {
  return adminApi('/admin/llm/alerts', {
    method: 'PUT',
    body: JSON.stringify({ dailyCostAlertUsd }),
  })
}

export interface PendingApprovalMessage {
  id: string
  body: string
  fromNumber: string
  toNumber: string
  createdAt: string
  accountId: string
  conversationId: string
  toolCall: any
  customerName: string | null
  customerPhone: string
  accountName: string
  businessName: string
  assignedAgentId: string | null
  assignedAgentName: string | null
  status: string
  proposedToolCall: { name: string; arguments: Record<string, any>; callId?: string; preview?: string; pendingReplyText?: string } | null
  conversationMessages: Array<{ id: string; body: string; direction: string; createdAt: string }>
}

export function listPendingApprovalMessages(): Promise<PendingApprovalMessage[]> {
  return adminApi('/admin/queue/pending')
}

export function approveMessage(id: string, editedBody?: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/queue/messages/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ editedBody }),
  })
}

export function rejectMessage(id: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/queue/messages/${id}/reject`, { method: 'POST' })
}

export function approveToolCall(id: string, editedArgs?: Record<string, unknown>): Promise<{ success: boolean }> {
  return adminApi(`/admin/queue/tools/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ editedArgs }),
  })
}

export function rejectToolCall(id: string, manualReply?: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/queue/tools/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ manualReply }),
  })
}

// ── LLM Logs ─────────────────────────────────────────────────────────────────

export interface LlmInteractionLog {
  id: string
  messageId: string | null
  model: string
  systemPrompt: string
  inputMessages: any
  rawResponse: any
  toolCalls: any
  finalReply: string | null
  iterations: number
  durationMs: number
  approvedBy: string | null
  editedBody: string | null
  createdAt: string
}

export function getConversationLlmLogs(conversationId: string): Promise<LlmInteractionLog[]> {
  return adminApi(`/admin/conversations/${conversationId}/llm-logs`)
}

// ── Conversation Ownership ───────────────────────────────────────────────────

export function acceptConversation(id: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/conversations/${id}/accept`, { method: 'POST' })
}

export function releaseConversation(id: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/conversations/${id}/release`, { method: 'POST' })
}

export function takeoverConversation(id: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/conversations/${id}/takeover`, { method: 'POST' })
}

// ── Invoice Assignment ──────────────────────────────────────────────────────

export function assignInvoice(id: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/invoices/${id}/assign`, { method: 'POST' })
}

export function releaseInvoiceAssignment(id: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/invoices/${id}/release`, { method: 'POST' })
}

export function takeoverInvoice(id: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/invoices/${id}/takeover`, { method: 'POST' })
}

// ── Platform Invoices (Ten Four → account) ────────────────────────────────
// BringCorp's own audit trail of what we billed each account for.

export interface AdminPlatformInvoice {
  id: string
  invoiceNumber: string
  accountId: string
  accountName: string
  accountEmail: string
  periodStart: string
  periodEnd: string
  subscriptionAmount: number
  invoiceCount: number
  perInvoiceFee: number
  totalAmount: number
  status: 'DRAFT' | 'PAID' | 'FAILED' | 'VOID'
  stripePaymentIntentId: string | null
  failureReason: string | null
  pdfUrl: string | null
  createdAt: string
  paidAt: string | null
}

export interface AdminPlatformInvoiceListParams {
  accountId?: string
  accountQuery?: string
  status?: string
  from?: string
  to?: string
  limit?: number
  offset?: number
}

export function listPlatformInvoices(params: AdminPlatformInvoiceListParams = {}): Promise<{
  total: number
  limit: number
  offset: number
  items: AdminPlatformInvoice[]
}> {
  const q = new URLSearchParams()
  if (params.accountId) q.set('accountId', params.accountId)
  if (params.accountQuery) q.set('accountQuery', params.accountQuery)
  if (params.status) q.set('status', params.status)
  if (params.from) q.set('from', params.from)
  if (params.to) q.set('to', params.to)
  if (params.limit !== undefined) q.set('limit', String(params.limit))
  if (params.offset !== undefined) q.set('offset', String(params.offset))
  const qs = q.toString()
  return adminApi(`/admin/platform-invoices${qs ? `?${qs}` : ''}`)
}

export interface AdminPlatformInvoiceDetail extends AdminPlatformInvoice {
  hasPaymentMethod: boolean
  /** Voice/SMS usage overage billed on this invoice (tier-config Phase 5);
   *  0 for unmetered tiers. totalAmount already includes them. */
  voiceOverageUsd?: number
  smsOverageUsd?: number
  /** Quantities behind the overage amounts (from the linked
   *  usage_overage_charges claim) — drives the Qty × unit-price rows (#319).
   *  Null for invoices with no overage claim. */
  overage?: {
    monthKey: string
    voiceOverageMinutes: number
    voiceOverageHours: number
    smsOverageCount: number
    smsOverageBlocks: number
  } | null
  billingCycle: {
    id: string
    periodStart: string
    periodEnd: string
    invoiceCount: number
    status: string
  } | null
}

export function getPlatformInvoice(id: string): Promise<AdminPlatformInvoiceDetail> {
  return adminApi(`/admin/platform-invoices/${id}`)
}

/**
 * #321 — the human review gate: charge a reviewed DRAFT (or retry a FAILED)
 * platform invoice. The billing scanner no longer auto-charges at cycle close;
 * an admin approves the charge here.
 */
export function chargePlatformInvoice(id: string): Promise<{ enqueued: boolean; invoiceNumber: string }> {
  return adminApi(`/admin/platform-invoices/${id}/charge`, { method: 'POST' })
}

/**
 * Triggers a CSV download of platform invoices matching the given filters.
 * Fetches with the admin JWT in the Authorization header (a plain href download
 * wouldn't carry it and would 401), then creates a blob URL for the browser
 * to download.
 */
export async function downloadPlatformInvoicesCsv(params: AdminPlatformInvoiceListParams = {}): Promise<void> {
  const q = new URLSearchParams()
  if (params.accountId) q.set('accountId', params.accountId)
  if (params.accountQuery) q.set('accountQuery', params.accountQuery)
  if (params.status) q.set('status', params.status)
  if (params.from) q.set('from', params.from)
  if (params.to) q.set('to', params.to)
  const qs = q.toString()
  const url = `${API_BASE}/admin/platform-invoices.csv${qs ? `?${qs}` : ''}`

  const token = getAdminToken()
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) throw new Error(`CSV download failed (${res.status})`)

  const blob = await res.blob()
  const blobUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = `tenfour-platform-invoices-${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(blobUrl)
}

// ── Self: profile + notification preferences ──────────────────────────────────

/** One review-email boolean per review-item flavor (ADMIN_REVIEW_EMAIL_PREF_FIELDS). */
export interface AdminReviewEmailPrefs {
  adminEmailOnInvoiceReview: boolean
  adminEmailOnVoiceReview: boolean
  adminEmailOnJobReview: boolean
  adminEmailOnBookingReview: boolean
  adminEmailOnToolCallReview: boolean
}

export interface AdminMe extends AdminReviewEmailPrefs {
  id: string
  email: string
  name: string | null
  ownerPhone: string | null
  oncallActive: boolean
  adminPushMuted: boolean
  adminSmsEnabled: boolean
}

export function getAdminMe(): Promise<AdminMe> {
  return adminApi<AdminMe>('/admin/me')
}

export function updateAdminNotificationPrefs(
  prefs: {
    adminPushMuted?: boolean
    adminSmsEnabled?: boolean
    ownerPhone?: string
  } & Partial<AdminReviewEmailPrefs>,
): Promise<
  {
    id: string
    ownerPhone: string | null
    adminPushMuted: boolean
    adminSmsEnabled: boolean
  } & AdminReviewEmailPrefs
> {
  return adminApi('/admin/me/notifications', { method: 'PATCH', body: JSON.stringify(prefs) })
}

// ── Numbers (Twilio number inventory) ─────────────────────────────────────────
// Read-only inventory for now. Procure/activate/release/capability-toggle land
// in a later phase.

import type {
  DemoContent,
  DemoMode,
  DemoPersonasOverride,
  DemoScriptOverride,
  DemoPromptsOverride,
  VoiceProfile,
  VoiceProfileKind,
} from '@tenfour/contracts'

export type {
  DemoContent,
  DemoMode,
  VoiceProfile,
  VoiceProfileKind,
} from '@tenfour/contracts'

export interface AdminNumber {
  accountId: string
  phone: string
  type: 'demo' | 'outreach' | 'account'
  assignedTo: string
  trade: string | null
  status: 'active' | 'inactive'
  a2pCampaign: string | null
  demoConfigId: string | null
  // The outreach persona template this number speaks as (outreach numbers only).
  outreachTemplateName: string | null
  capabilities: { sms: boolean; voice: boolean }
  twilioSid: string | null
}

export function getNumbers(): Promise<{ numbers: AdminNumber[] }> {
  return adminApi('/admin/numbers')
}

// A reusable demo definition, for the Numbers "Assign demo" picker.
export interface DemoDefinition {
  demoConfigId: string
  label: string
  trade: string | null
  mode: DemoMode
  enabled: boolean
  numberCount: number
}

export function getDemoDefinitions(): Promise<{ definitions: DemoDefinition[] }> {
  return adminApi('/admin/numbers/demo-definitions')
}

// Provision a new demo or outreach number (the worker purchases it +
// temporarily attaches both kinds to the demo A2P campaign; see
// plans/outreach-persona.md). Demo numbers may link a reusable demo definition;
// outreach numbers may point at an outreach persona template.
export function createNumber(payload: {
  kind?: 'demo' | 'outreach'
  demoConfigId?: string | null
  outreachTemplateName?: string | null
  label?: string
}): Promise<{ success: boolean; accountId: string }> {
  return adminApi('/admin/numbers', { method: 'POST', body: JSON.stringify(payload) })
}

// Link (demoConfigId) or unlink (null) the demo definition a number serves.
export function assignDemoConfig(
  accountId: string,
  demoConfigId: string | null,
): Promise<{ success: boolean; demoConfigId: string | null }> {
  return adminApi(`/admin/numbers/${accountId}/demo-config`, {
    method: 'PUT',
    body: JSON.stringify({ demoConfigId }),
  })
}

// Point (templateName) or un-point (null) an outreach number at an outreach
// persona template.
export function assignOutreachTemplate(
  accountId: string,
  templateName: string | null,
): Promise<{ success: boolean; outreachTemplateName: string | null }> {
  return adminApi(`/admin/numbers/${accountId}/outreach-template`, {
    method: 'PUT',
    body: JSON.stringify({ templateName }),
  })
}

// Release a number from Twilio (any number). The worker calls Twilio then clears
// the phone + SID on the row.
export function releaseNumber(accountId: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/numbers/${accountId}/release`, { method: 'POST' })
}

// Switch a number's A2P campaign ('master' ↔ 'demo'). The worker detaches from
// the current MessagingService and attaches to the new one.
export function switchCampaign(
  accountId: string,
  toCampaign: 'master' | 'demo',
): Promise<{ success: boolean; toCampaign: string }> {
  return adminApi(`/admin/numbers/${accountId}/switch-campaign`, {
    method: 'POST',
    body: JSON.stringify({ toCampaign }),
  })
}

// ── Demos (the editable per-prospect demo definitions) ────────────────────────

export interface AdminDemoListItem {
  accountId: string
  businessName: string
  label: string | null
  trade: string | null
  mode: DemoMode
  enabled: boolean
  configured: boolean
  updatedAt: string | null
}

export function getDemos(): Promise<{ demos: AdminDemoListItem[] }> {
  return adminApi('/admin/numbers/demos')
}

// A2P-registered campaign samples — surfaced read-only, never per-demo editable.
export interface DemoA2pLocked {
  confirmationBody: string
  helpBody: string
}

export type DemoLlmEffective = {
  provider: string
  model: string
  baseUrl: string | null
  credential: string | null
  capabilities?: {
    temperature?: string
    toolChoice?: string
    reasoningEffort?: string[] | null
  }
  temperature: number | null
  toolChoice: string | null
  reasoningEffort: string | null
  maxIterations: number | null
  maxTokens: number | null
  fallbackProvider: string | null
  fallbackModel: string | null
  enabled: boolean
}

export type DemoVoiceLlmEffective = {
  provider: string
  model: string | null
  voice: string | null
  reasoningEffort: string | null
}

export interface DemoNumberUsage {
  accountId: string
  phone: string
  label: string
}

export interface AdminDemoConfig {
  accountId: string
  businessName: string
  demoConfigId: string | null
  usedByNumberCount: number
  usedByNumbers: DemoNumberUsage[]
  label: string | null
  trade: string | null
  mode: DemoMode
  enabled: boolean
  voiceIntakeProfileName: string | null
  voiceAriaProfileName: string | null
  voiceLeanProfileName: string | null
  llmOverride: import('@tenfour/contracts').DemoLlmOverride
  voiceLlmOverride: import('@tenfour/contracts').DemoVoiceLlmOverride
  llmEffective: DemoLlmEffective
  voiceLlmEffective: DemoVoiceLlmEffective
  llmInheritsFrom: 'global_demo_role'
  // The stored override (only fields the admin has changed).
  override: {
    personas: DemoPersonasOverride | null
    script: DemoScriptOverride | null
    prompts: DemoPromptsOverride | null
  }
  // Override deep-merged over defaults — the effective content the worker renders.
  resolved: DemoContent
  // Code defaults — placeholder / reset-to-default reference.
  defaults: DemoContent
  a2pLocked: DemoA2pLocked
}

export function getDemoConfig(accountId: string): Promise<AdminDemoConfig> {
  return adminApi(`/admin/numbers/demos/${accountId}`)
}

// PUT body — only changed fields are sent. Note: the message-templates override
// is `smsScript` (NOT `script`).
export interface PutDemoConfigPayload {
  label?: string | null
  trade?: string | null
  mode?: DemoMode
  personas?: DemoPersonasOverride
  smsScript?: DemoScriptOverride
  prompts?: DemoPromptsOverride
  voiceIntakeProfileName?: string | null
  voiceAriaProfileName?: string | null
  voiceLeanProfileName?: string | null
  llmOverride?: import('@tenfour/contracts').DemoLlmOverride
  voiceLlmOverride?: import('@tenfour/contracts').DemoVoiceLlmOverride
  enabled?: boolean
}

export function putDemoConfig(
  accountId: string,
  body: PutDemoConfigPayload,
): Promise<{ success: boolean; config: AdminDemoConfig }> {
  return adminApi(`/admin/numbers/demos/${accountId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export function createDemo(
  body?: { label?: string },
): Promise<{ success: boolean; demo: { accountId: string; label: string } }> {
  return adminApi('/admin/numbers/demos', {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  })
}

export function copyDemo(
  accountId: string,
): Promise<{ success: boolean; demo: { accountId: string; label: string } }> {
  return adminApi(`/admin/numbers/demos/${accountId}/copy`, { method: 'POST' })
}

export function deleteDemo(accountId: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/numbers/demos/${accountId}`, { method: 'DELETE' })
}

// ── Voice profiles (DB-backed mirror of the voice-agent profile shape) ────────

// List row — enriched with library metadata + usage (which demos link it, via a
// binding OR a demo number's own voiceProfileId fallback).
export interface VoiceProfileListItem {
  name: string
  displayName: string
  kind: VoiceProfileKind
  enabled: boolean
  source: 'bundled' | 'custom'
  updatedAt: string
  usedByDemoCount: number
  usedByDemoLabels: string[]
}

export function getVoiceProfiles(): Promise<{ profiles: VoiceProfileListItem[] }> {
  return adminApi('/admin/numbers/voice-profiles')
}

export function getVoiceProfile(name: string): Promise<VoiceProfile> {
  return adminApi(`/admin/numbers/voice-profiles/${name}`)
}

export type PutVoiceProfilePayload = Omit<VoiceProfile, 'name'>

export function putVoiceProfile(
  name: string,
  body: PutVoiceProfilePayload,
): Promise<{ success: boolean; profile: VoiceProfile }> {
  return adminApi(`/admin/numbers/voice-profiles/${name}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

// Create blank, from a bundled `templateName`, or as a copy of `copyFrom`.
export function createVoiceProfile(body: {
  name: string
  displayName?: string
  templateName?: string
  copyFrom?: string
}): Promise<{ success: boolean; profile: VoiceProfile }> {
  return adminApi('/admin/numbers/voice-profiles', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function copyVoiceProfile(
  name: string,
  body: { newName: string; displayName?: string },
): Promise<{ success: boolean; profile: VoiceProfile }> {
  return adminApi(`/admin/numbers/voice-profiles/${name}/copy`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function deleteVoiceProfile(name: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/numbers/voice-profiles/${name}`, { method: 'DELETE' })
}

export function resetBundledProfile(
  name: string,
): Promise<{ success: boolean; profile: VoiceProfile }> {
  return adminApi(`/admin/numbers/voice-profiles/${name}/reset-bundled`, { method: 'POST' })
}

// ── "Add to seed" exports (paste-into-an-AI-assistant snippets) ───────────────

export function getVoiceProfileSeedExport(name: string): Promise<{ markdown: string }> {
  return adminApi(`/admin/numbers/voice-profiles/${name}/seed-export`)
}

export type DemoSeedExportMode = 'override' | 'resolved' | 'full'

export function getDemoSeedExport(
  accountId: string,
  mode: DemoSeedExportMode = 'override',
): Promise<{ markdown: string; mode: DemoSeedExportMode }> {
  return adminApi(`/admin/numbers/demos/${accountId}/seed-export?mode=${mode}`)
}

// ── Outreach sign-ups audit (/admin/signups — plans/outreach-signups-audit-page.md)

export type OutreachSignupListParams = {
  /** An OutreachSessionStatus, or the virtual filters called | viewed | expired. */
  status?: string
  cursor?: string
  limit?: number
  q?: string
}

export function listOutreachSignups(
  params: OutreachSignupListParams = {},
): Promise<import('@tenfour/contracts').AdminOutreachSignupList> {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.cursor) qs.set('cursor', params.cursor)
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.q) qs.set('q', params.q)
  const suffix = qs.size ? `?${qs.toString()}` : ''
  return adminApi(`/admin/outreach/signups${suffix}`)
}

export function getOutreachSignupStats(): Promise<
  import('@tenfour/contracts').AdminOutreachFunnelStats
> {
  return adminApi('/admin/outreach/signups/stats')
}

export function getOutreachSignupDetail(
  id: string,
): Promise<import('@tenfour/contracts').AdminOutreachSignupDetail> {
  return adminApi(`/admin/outreach/signups/${id}`)
}

// ── Inference reviews (plans/inference-review-tool-call-queue.md) ─────────────
// The reviewable unit is an inference_review_items row (one mutating tool call
// or one whole voice call). Types are shared with the API via
// @tenfour/contracts (inference-review.ts).

/** Server-side filters the pending queue accepts (a subset of the history's). */
export type ReviewQueueFilters = Pick<ReviewHistoryFilters, 'flavor' | 'toolName' | 'accountId'>

export function getReviewQueue(
  params?: { cursor?: string; limit?: number } & ReviewQueueFilters,
): Promise<{ items: ReviewQueueItem[]; nextCursor: string | null }> {
  const qs = reviewFilterQuery(params)
  return adminApi(`/admin/reviews/queue${qs ? `?${qs}` : ''}`)
}

export function getReviewStats(): Promise<ReviewStats> {
  return adminApi('/admin/reviews/stats')
}

export function getReviewStates(logIds: string[]): Promise<Record<string, ReviewState>> {
  return adminApi(`/admin/reviews/state?logIds=${encodeURIComponent(logIds.join(','))}`)
}

/** Detail for one reviewable unit — accepts an item id or a legacy log id. */
export function getReviewItemDetail(idOrLogId: string): Promise<InferenceReviewItemDetail> {
  return adminApi(`/admin/reviews/items/${idOrLogId}`)
}

export function submitInferenceReview(
  itemId: string,
  body: SubmitInferenceReview,
): Promise<{ reviews: InferenceReviewItem[] }> {
  return adminApi(`/admin/reviews/items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

/**
 * IN_REVIEW invoices with no linked review item (direct-POST /
 * pre-epoch) — highlighted rows at the head of the Reviews queue, each
 * navigating to the standalone invoice review page.
 */
export function getUnlinkedPendingInvoices(cursor?: string): Promise<{
  invoices: Array<{
    invoice: ReviewInvoicePayload
    accountId: string
    accountName: string | null
    businessName: string | null
    createdAt: string
  }>
  nextCursor: string | null
}> {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''
  return adminApi(`/admin/reviews/pending-invoices${qs}`)
}

/** One invoice by id (any status) — the standalone approval page's fetch. */
export function getReviewInvoiceDetail(invoiceId: string): Promise<ReviewInvoiceDetail> {
  return adminApi(`/admin/reviews/invoices/${invoiceId}`)
}

function reviewFilterQuery(params: ReviewHistoryFilters = {}): string {
  const q = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== '') q.set(key, String(value))
  }
  return q.toString()
}

export function getReviewHistory(params: ReviewHistoryFilters = {}): Promise<ReviewHistoryResponse> {
  const query = reviewFilterQuery(params)
  return adminApi(`/admin/reviews/history${query ? `?${query}` : ''}`)
}
/**
 * #499 — ops releases of IN_REVIEW invoices, listed in the history tab beside
 * the AI grading records. Bounded, not cursor-paged: it is the tail of ops
 * activity, and a second cursor in the same view would make the two lists
 * disagree about where a page ends.
 */
export function getReviewInvoiceReleases(params: {
  accountId?: string
  reviewerUserId?: string
  from?: string
  to?: string
}): Promise<ReviewInvoiceRelease[]> {
  const q = new URLSearchParams()
  if (params.accountId) q.set('accountId', params.accountId)
  if (params.reviewerUserId) q.set('reviewerUserId', params.reviewerUserId)
  if (params.from) q.set('from', params.from)
  if (params.to) q.set('to', params.to)
  const query = q.toString()
  return adminApi(`/admin/reviews/invoice-releases${query ? `?${query}` : ''}`)
}


/** Distinct admins with submitted reviews — feeds the history reviewer filter. */
export function getReviewReviewers(): Promise<{ reviewers: InferenceReviewerRef[] }> {
  return adminApi('/admin/reviews/reviewers')
}

export async function downloadReviewExport(params: ReviewHistoryFilters = {}): Promise<void> {
  const { cursor: _cursor, limit: _limit, ...filters } = params
  const query = reviewFilterQuery(filters)
  const response = await adminFetchRaw(`/admin/reviews/export.jsonl${query ? `?${query}` : ''}`)
  if (!response.ok) throw new Error(`Export failed (${response.status})`)
  const blob = await response.blob()
  const disposition = response.headers.get('content-disposition') ?? ''
  const filename = disposition.match(/filename="?([^";]+)"?/)?.[1] ?? 'tenfour-inference-reviews.jsonl'
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

// ── Form templates (the booking-form builder library) ─────────────────────────
// Reusable booking-form question sets. The seeded `isDefault` template is the
// read-only default assigned to every account; custom forks are `source:'custom'`.

import type { QuestionType } from '@tenfour/contracts'

// A single question on a form template. `key` non-null marks a fixed "core"
// question bound to the structured booking intake (first_name, phone, street…);
// null marks a custom admin-added question.
export interface AdminFormQuestion {
  id: string
  order: number
  label: string
  type: QuestionType
  required: boolean
  options: string[]
  key: string | null
  placeholder: string | null
}

// What a form DOES when it's filled in: a BOOKING form creates a job (and is the
// only kind that can be an account's default booking form); an OTHER form just
// records a response.
export type FormTemplateKind = 'BOOKING' | 'OTHER'

// List-row shape (omits questions; carries the at-a-glance counts).
export interface AdminFormTemplateListItem {
  id: string
  name: string
  isDefault: boolean
  source: 'bundled' | 'custom'
  // BOOKING forms create a job (only these can be an account's default); OTHER
  // forms only record a response — Lyra sends them by name.
  kind: FormTemplateKind
  enabled: boolean
  updatedAt: string
  questionCount: number
  usedByAccountCount: number
  usedByAccountLabels: string[]
}

// Full template incl. its ordered questions (the editor's load shape).
export interface AdminFormTemplateDetail {
  id: string
  name: string
  isDefault: boolean
  source: 'bundled' | 'custom'
  // BOOKING forms create a job; OTHER forms only record a response.
  kind: FormTemplateKind
  enabled: boolean
  updatedAt: string
  questions: AdminFormQuestion[]
  usedByAccountCount: number
}

// Editable question input (no id/order — order is the array position).
export interface FormQuestionPayload {
  label: string
  type: QuestionType
  required: boolean
  options: string[]
  key: string | null
  placeholder: string | null
}

export interface FormTemplatePayload {
  name: string
  // What the form DOES when submitted. The API defaults it to BOOKING, but it
  // refuses a change once the template has responses (or while an account still
  // uses it as their default booking form) — so send it explicitly.
  kind: FormTemplateKind
  enabled: boolean
  questions: FormQuestionPayload[]
}

export function getFormTemplates(): Promise<{ templates: AdminFormTemplateListItem[] }> {
  return adminApi('/admin/form-templates')
}

export function getFormTemplate(id: string): Promise<AdminFormTemplateDetail> {
  return adminApi(`/admin/form-templates/${id}`)
}

export function createFormTemplate(
  body: FormTemplatePayload,
): Promise<{ success: boolean; template: AdminFormTemplateDetail }> {
  return adminApi('/admin/form-templates', { method: 'POST', body: JSON.stringify(body) })
}

export function updateFormTemplate(
  id: string,
  body: FormTemplatePayload,
): Promise<{ success: boolean; template: AdminFormTemplateDetail }> {
  return adminApi(`/admin/form-templates/${id}`, { method: 'PUT', body: JSON.stringify(body) })
}

// Delete a custom template. When accounts are still assigned it, pass
// `reassignTo` (another template id) — the backend moves those accounts onto it
// before dropping this one. The bundled default is never deletable.
export function deleteFormTemplate(
  id: string,
  reassignTo?: string,
): Promise<{ success: boolean }> {
  const qs = reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : ''
  return adminApi(`/admin/form-templates/${id}${qs}`, { method: 'DELETE' })
}

// Make this template the account's DEFAULT booking form. Also adds it to the
// account's assigned set, so this is the one-call "use this form" action.
export function assignFormTemplate(id: string, accountId: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/form-templates/${id}/assign`, {
    method: 'POST',
    body: JSON.stringify({ accountId }),
  })
}

// ── Status console (/admin/status — plans/help-support-status.md) ─────────────
// Ops-triggered incidents behind the public tenfour.chat/status page.

export interface AdminStatusIncident {
  id: string
  component: string
  severity: 'OUTAGE' | 'DEGRADED'
  title: string
  message: string | null
  startsAt: string
  endsAt: string | null
  resolvedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface AdminStatusIncidentPayload {
  component: string
  severity: 'OUTAGE' | 'DEGRADED'
  title: string
  message?: string
  startsAt: string
  endsAt?: string | null
}

export interface AdminStatusAuditRow {
  id: string
  incidentId: string | null
  actorId: string | null
  actorName: string | null
  action: string
  before: unknown
  after: unknown
  createdAt: string
}

export function adminListStatusIncidents(): Promise<AdminStatusIncident[]> {
  return adminApi('/admin/status-incidents')
}

export function adminCreateStatusIncident(
  body: AdminStatusIncidentPayload,
): Promise<AdminStatusIncident> {
  return adminApi('/admin/status-incidents', { method: 'POST', body: JSON.stringify(body) })
}

export function adminUpdateStatusIncident(
  id: string,
  body: Partial<AdminStatusIncidentPayload>,
): Promise<AdminStatusIncident> {
  return adminApi(`/admin/status-incidents/${id}`, { method: 'PUT', body: JSON.stringify(body) })
}

export function adminResolveStatusIncident(id: string): Promise<AdminStatusIncident> {
  return adminApi(`/admin/status-incidents/${id}/resolve`, { method: 'POST' })
}

export function adminDeleteStatusIncident(id: string): Promise<{ ok: boolean }> {
  return adminApi(`/admin/status-incidents/${id}`, { method: 'DELETE' })
}

export function adminStatusIncidentAudit(limit = 50): Promise<AdminStatusAuditRow[]> {
  return adminApi(`/admin/status-incidents/audit?limit=${limit}`)
}

// ── Needs You feedback audit ────────────────────────────────────────────────

export interface AdminNeedsYouFeedbackParams {
  resolution?: 'DISMISSED' | 'REPLIED'
  feedback?: 'all' | 'with' | 'without'
  q?: string
  cursor?: string
  limit?: number
}

export function listNeedsYouFeedback(
  params: AdminNeedsYouFeedbackParams = {},
): Promise<AdminNeedsYouResolutionList> {
  const query = new URLSearchParams()
  if (params.resolution) query.set('resolution', params.resolution)
  if (params.feedback && params.feedback !== 'all') query.set('feedback', params.feedback)
  if (params.q) query.set('q', params.q)
  if (params.cursor) query.set('cursor', params.cursor)
  if (params.limit) query.set('limit', String(params.limit))
  const suffix = query.toString()
  return adminApi(`/admin/needs-you-feedback${suffix ? `?${suffix}` : ''}`)
}

export function getNeedsYouFeedbackStats(): Promise<AdminNeedsYouResolutionStats> {
  return adminApi('/admin/needs-you-feedback/stats')
}

export function getNeedsYouFeedbackDetail(
  id: string,
): Promise<AdminNeedsYouResolutionDetail> {
  return adminApi(`/admin/needs-you-feedback/${id}`)
}

// One of the account's assigned forms. `isAccountDefault` is this account's
// choice of default form; `isDefault` is the platform-wide bundled library
// default — a template can be either, both, or neither.
export interface AdminAccountFormItem {
  id: string
  name: string
  isDefault: boolean
  source: 'bundled' | 'custom'
  kind: FormTemplateKind
  enabled: boolean
  updatedAt: string
  questionCount: number
  assignedAt: string
  isAccountDefault: boolean
}

export function getAccountForms(
  accountId: string,
): Promise<{ defaultFormTemplateId: string | null; forms: AdminAccountFormItem[] }> {
  return adminApi(`/admin/form-templates/accounts/${accountId}`)
}

// Add a template to the account's assigned set, leaving the default alone.
// Idempotent — re-adding an already-assigned form is a no-op.
export function addFormToAccount(id: string, accountId: string): Promise<{ success: boolean }> {
  return adminApi(`/admin/form-templates/${id}/accounts`, {
    method: 'POST',
    body: JSON.stringify({ accountId }),
  })
}

// Drop a template from the account's assigned set. The API refuses to remove the
// account's default form — set a different default first.
export function removeFormFromAccount(
  id: string,
  accountId: string,
): Promise<{ success: boolean }> {
  return adminApi(`/admin/form-templates/${id}/accounts/${accountId}`, { method: 'DELETE' })
}

export function acceptAdminVerification(data: { token?: string; accessToken?: string; user?: any }) {
  if (data.user?.role !== 'ADMIN') throw new Error('Admin access required')
  const token = data.accessToken || data.token
  if (!token) throw new Error('Verification did not return a session')
  localStorage.setItem(ADMIN_TOKEN_KEY, token)
  localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(data.user))
}
